import { Currency, ETHER, Token } from '@uniswap/sdk'
import React, { useMemo } from 'react'
import styled from 'styled-components'

import EthereumLogo from '../../assets/images/ethereum-logo.png'
import useHttpLocations from '../../hooks/useHttpLocations'
import { WrappedTokenInfo, useSelectedTokenList } from '../../state/lists/hooks'
import { WETH_CONFLUX_TESTNET } from '../../constants'
import { useActiveWeb3React } from '../../hooks'
import Logo from '../Logo'

const getTokenLogoURL = (address: string) =>
  `https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/ethereum/assets/${address}/logo.png`

const StyledEthereumLogo = styled.img<{ size: string }>`
  width: ${({ size }) => size};
  height: ${({ size }) => size};
  box-shadow: 0px 6px 10px rgba(0, 0, 0, 0.075);
  border-radius: 24px;
`

const StyledLogo = styled(Logo)<{ size: string }>`
  width: ${({ size }) => size};
  height: ${({ size }) => size};
`

// Check if token is WETH on Conflux Testnet
const isConfluxWETH = (currency?: Currency): boolean => {
  if (!(currency instanceof Token)) return false
  return currency.address.toLowerCase() === WETH_CONFLUX_TESTNET.address.toLowerCase()
}

export default function CurrencyLogo({
  currency,
  size = '24px',
  style
}: {
  currency?: Currency
  size?: string
  style?: React.CSSProperties
}) {
  const { chainId } = useActiveWeb3React()
  const tokenList = useSelectedTokenList()

  // Get logoURI from WrappedTokenInfo or look up in token list
  const logoURI = useMemo(() => {
    if (currency instanceof WrappedTokenInfo) {
      return currency.logoURI
    }
    // Look up in token list by address (case-insensitive)
    if (currency instanceof Token && chainId) {
      const chainTokens = tokenList[chainId]
      if (chainTokens) {
        // Try exact match first
        if (chainTokens[currency.address]) {
          return chainTokens[currency.address].logoURI
        }
        // Try case-insensitive match
        const lowerAddress = currency.address.toLowerCase()
        const matchedAddress = Object.keys(chainTokens).find(
          addr => addr.toLowerCase() === lowerAddress
        )
        if (matchedAddress) {
          return chainTokens[matchedAddress].logoURI
        }
      }
    }
    return undefined
  }, [currency, chainId, tokenList])

  const uriLocations = useHttpLocations(logoURI)

  const srcs: string[] = useMemo(() => {
    if (currency === ETHER) return []

    if (currency instanceof Token) {
      if (uriLocations.length > 0) {
        return [...uriLocations, getTokenLogoURL(currency.address)]
      }
      return [getTokenLogoURL(currency.address)]
    }
    return []
  }, [currency, uriLocations])

  // Use ETH logo for native currency and WETH on Conflux
  if (currency === ETHER || isConfluxWETH(currency)) {
    return <StyledEthereumLogo src={EthereumLogo} size={size} style={style} />
  }

  return <StyledLogo size={size} srcs={srcs} alt={`${currency?.symbol ?? 'token'} logo`} style={style} />
}
